import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { useMarketplace } from '../../context/MarketplaceContext';
import { useCoins } from '../../context/CoinContext';
import { checkCoinRequirement, MARKETPLACE_FEES } from '../../utils/marketplaceFees';
import AuctionManagementModal from './AuctionManagementModal';

const AuctionDetail = () => {
  const { t } = useTranslation();
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { placeBid } = useMarketplace();
  const { coins } = useCoins();
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState('');
  const [placingBid, setPlacingBid] = useState(false);
  const [showManagementModal, setShowManagementModal] = useState(false);

  useEffect(() => {
    if (auctionId) {
      const unsubscribe = onSnapshot(
        doc(db, 'auctions', auctionId),
        (snapshot) => {
          if (snapshot.exists()) {
            setAuction({ id: snapshot.id, ...snapshot.data() });
          }
          setLoading(false);
        },
        (error) => {
          console.error('Error fetching auction:', error);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    }
  }, [auctionId]);

  const handlePlaceBid = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      alert(t('auction.pleaseLogin'));
      return;
    }

    const bidValue = parseFloat(bidAmount);
    if (isNaN(bidValue) || bidValue <= auction.currentBid) {
      alert(t('auction.pleaseBidHigher', { amount: auction.currentBid?.toLocaleString() }));
      return;
    }

    const feeCheck = checkCoinRequirement(coins, 'PLACE_BID');
    if (!feeCheck.hasEnough) {
      alert(feeCheck.message);
      return;
    }

    setPlacingBid(true);
    try {
      await placeBid(auctionId, bidValue);
      alert(t('auction.bidSuccess'));
      setBidAmount('');
    } catch (error) {
      alert(t('common.saveError') + error.message);
    } finally {
      setPlacingBid(false);
    }
  };

  const timeRemaining = auction?.endTime
    ? Math.max(0, Math.floor((new Date(auction.endTime.toDate()) - new Date()) / 1000 / 60 / 60))
    : null;

  const formatTimeRemaining = (hours) => {
    if (hours <= 0) return t('auction.expired');
    if (hours < 24) return `${hours} ${t('auction.hours')}`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days} ${t('auction.days')} ${remainingHours} ${t('auction.hours')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">{t('auction.auctionNotFound')}</p>
          <button
            onClick={() => navigate('/marketplace')}
            className="mt-4 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  const isEnded = auction.isEnded || (timeRemaining !== null && timeRemaining <= 0);
  const isMyAuction = currentUser?.uid === auction.sellerId;
  const isHighestBidder = currentUser?.uid === auction.highestBidder;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">{auction.productName}</h1>
              <p className="text-gray-600">{t('auction.seller')}: {auction.sellerId}</p>
            </div>
            <div className={`px-4 py-2 rounded-full text-sm font-semibold ${
              isEnded ? 'bg-gray-100 text-gray-800' : 'bg-yellow-100 text-yellow-800'
            }`}>
              {isEnded ? t('auction.auctionEnded') : `${t('auction.timeRemaining')}: ${formatTimeRemaining(timeRemaining)}`}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">{t('auction.startingPrice')}</h3>
              <p className="text-2xl font-bold text-gray-800">฿{auction.startingPrice?.toLocaleString()}</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">{t('auction.currentPrice')}</h3>
              <p className="text-2xl font-bold text-yellow-600">฿{auction.currentBid?.toLocaleString()}</p>
            </div>
            {auction.reservePrice && (
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">{t('auction.reservePrice')}</h3>
                <p className="text-xl font-bold text-gray-800">฿{auction.reservePrice?.toLocaleString()}</p>
              </div>
            )}
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">{t('auction.numberOfBidders')}</h3>
              <p className="text-xl font-bold text-gray-800">{auction.bids?.length || 0} {t('common.people')}</p>
            </div>
          </div>

          {isHighestBidder && !isEnded && (
            <div className="mb-4 p-4 bg-green-50 rounded-lg">
              <p className="text-green-800 font-semibold">{t('auction.youAreHighestBidder')}</p>
            </div>
          )}

          {isMyAuction && !isEnded && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-blue-800 font-semibold">{t('auction.thisIsYourAuction')}</p>
            </div>
          )}

          {/* Place Bid Form */}
          {!isMyAuction && !isEnded && (
            <div className="mb-6 p-4 bg-yellow-50 rounded-lg">
              <h3 className="font-semibold text-gray-700 mb-2">{t('auction.placeBid')}</h3>
              <form onSubmit={handlePlaceBid} className="flex gap-2">
                <input
                  type="number"
                  required
                  min={auction.currentBid + 1}
                  step="0.01"
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  placeholder={`${t('auction.minimumBid')} ฿${(auction.currentBid + 1)?.toLocaleString()}`}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
                <button
                  type="submit"
                  disabled={placingBid}
                  className="bg-yellow-500 hover:bg-yellow-600 disabled:bg-gray-300 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
                >
                  {placingBid ? t('auction.placingBid') : `${t('auction.bid')} (${MARKETPLACE_FEES.PLACE_BID.default} ${t('auction.coins')})`}
                </button>
              </form>
              {currentUser && (
                <p className="text-sm text-gray-600 mt-2">
                  {t('auction.coinsAvailable')}: {coins} {t('auction.coins')}
                </p>
              )}
            </div>
          )}

          {/* Bids History */}
          {auction.bids && auction.bids.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-700 mb-2">{t('auction.bidHistory')}</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {[...auction.bids].reverse().map((bid, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg ${
                      bid.userId === auction.highestBidder ? 'bg-yellow-50 border-2 border-yellow-400' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold text-gray-800">
                          {bid.userName || t('auction.user')}
                          {bid.userId === currentUser?.uid && ` (${t('auction.you')})`}
                          {bid.userId === auction.highestBidder && ' 🏆'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {new Date(bid.timestamp?.toDate()).toLocaleString('th-TH')}
                        </p>
                      </div>
                      <p className="text-lg font-bold text-gray-800">฿{bid.amount?.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <button
              onClick={() => navigate('/marketplace')}
              className="border border-gray-300 hover:bg-gray-100 text-gray-700 font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              {t('common.back')}
            </button>
            {isMyAuction && (
              <button
                onClick={() => setShowManagementModal(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                {t('auction.manageAuction')}
              </button>
            )}
          </div>

        </div>

        {/* Auction Management Modal */}
        {isMyAuction && (
          <AuctionManagementModal
            auction={auction}
            isOpen={showManagementModal}
            onClose={() => setShowManagementModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default AuctionDetail;



